import { useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc } from "firebase/firestore";
import { storage, db } from "../../firebase-config";
import { v4 } from "uuid";

// Utility function to resize image (for lower quality)
const resizeImage = (file, quality) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                canvas.width = img.width * quality;
                canvas.height = img.height * quality;
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    if (blob) resolve(new File([blob], file.name, { type: file.type }));
                }, file.type, quality);
            };
        };
        reader.onerror = (error) => reject(error);
    });
};

const Backend = () => {
    const [productName, setProductName] = useState('');
    const [productPrice, setProductPrice] = useState(0);
    const [productImgs, setProductImgs] = useState([]); // Stores multiple files
    const [imgUrls, setImgUrls] = useState({ high: [], low: [] }); // High and Low-quality URLs
    const [category, setCategory] = useState(''); // Ornament category
    const [metalType, setMetalType] = useState(''); // Metal type
    const [purity, setPurity] = useState(''); // Metal purity
    const [error, setError] = useState('');

    const types = ['image/png', 'image/jpeg'];

    // Handle multiple image selection
    const productImgHandler = (e) => {
        let selectedFiles = Array.from(e.target.files);
        let validFiles = selectedFiles.filter(file => types.includes(file.type));

        if (validFiles.length) {
            setProductImgs(validFiles);
            setError('');
        } else {
            setProductImgs([]);
            setError('Please select valid image types (jpg or png)');
        }
    };

    // Add product
    const addProduct = async (e) => {
        e.preventDefault();
        if (!productImgs.length) return;

        try {
            // Loop through the selected images, upload them in two qualities
            const imgUploadPromises = productImgs.map(async (img) => {
                // High-quality upload
                const highQualityRef = ref(storage, `images/high-quality/${img.name + v4()}`);
                const highSnapshot = await uploadBytes(highQualityRef, img);
                const highQualityUrl = await getDownloadURL(highSnapshot.ref);

                // Low-quality upload (Resized image)
                const lowQualityImg = await resizeImage(img, 0.8); // Compress to 50% of original size
                const lowQualityRef = ref(storage, `images/low-quality/${lowQualityImg.name + v4()}`);
                const lowSnapshot = await uploadBytes(lowQualityRef, lowQualityImg);
                const lowQualityUrl = await getDownloadURL(lowSnapshot.ref);

                return { high: highQualityUrl, low: lowQualityUrl };
            });

            const imageUrls = await Promise.all(imgUploadPromises);

            // Add the product document with the URLs of uploaded images
            const usersCollectionRef = collection(db, "products");
            await addDoc(usersCollectionRef, {
                ProductName: productName,
                ProductPrice: Number(productPrice),
                ProductImgs: {
                    high: imageUrls.map(img => img.high),
                    low: imageUrls.map(img => img.low),
                },
                Category: category,
                MetalType: metalType,
                Purity: purity
            });

            // Reset form and state
            setProductName('');
            setProductPrice(0);
            setProductImgs([]);
            setCategory('');
            setMetalType('');
            setPurity('');
            setImgUrls({ high: [], low: [] });
            setError('');
            document.getElementById('file').value = '';
        } catch (error) {
            setError(error.message);
        }
    };

    return (
        <div className='container'>
            <br />
            <h2>ADD PRODUCTS</h2>
            <hr />
            <form autoComplete="off" className='form-group' onSubmit={addProduct}>
                {/* Category Selection */}
                <label htmlFor="category">Ornament Category</label>
                <select
                    className='form-control'
                    required
                    onChange={(e) => setCategory(e.target.value)}
                    value={category}
                >
                    <option value="">Select Category</option>
                    <option value="Bangle">Bangle</option>
                    <option value="Bracelet">Bracelet</option>
                    <option value="Necklace">Necklace</option>
                    {/* Add more categories as needed */}
                </select>

                <br />
                <label htmlFor="product-name">Product Name</label>
                <input
                    type="text"
                    className='form-control'
                    required
                    onChange={(e) => setProductName(e.target.value)}
                    value={productName}
                />

                <br />
                <label htmlFor="product-price">Product Price</label>
                <input
                    type="number"
                    className='form-control'
                    required
                    onChange={(e) => setProductPrice(e.target.value)}
                    value={productPrice}
                />

                <br />
                {/* Metal Type Selection */}
                <label htmlFor="metal-type">Metal Type</label>
                <select
                    className='form-control'
                    required
                    onChange={(e) => setMetalType(e.target.value)}
                    value={metalType}
                >
                    <option value="">Select Metal Type</option>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Platinum">Platinum</option>
                    {/* Add more metal types as needed */}
                </select>

                <br />
                {/* Purity Input */}
                <label htmlFor="purity">Purity (e.g., 24K, 22K)</label>
                <input
                    type="text"
                    className='form-control'
                    required
                    onChange={(e) => setPurity(e.target.value)}
                    value={purity}
                />

                <br />
                <label htmlFor="product-img">Product Images</label>
                <input
                    type="file"
                    className='form-control'
                    id="file"
                    multiple // Allows multiple file selection
                    required
                    onChange={productImgHandler}
                />

                <br />
                <button type="submit" className='btn btn-success btn-md mybtn'>ADD</button>
            </form>
            {error && <span className='error-msg'>{error}</span>}
        </div>
    );
};

export default Backend;
